import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Bell, Check, AlertCircle, Lock } from 'lucide-react';
import {
  fetchEmailPreferences,
  updateEmailPreferences,
} from '../../../redux/settingsPages/emailPreferences/emailPreferencesActions';
import { clearEmailPreferencesError } from '../../../redux/settingsPages/emailPreferences/emailPreferencesSlice';

// Order matters here: most-wanted toggle first, mandatory (read-only) fields last.
const TOGGLEABLE_FIELDS = [
  'weekly_digest',
  'community_updates',
  'price_alerts',
  'beta_invitations',
  'marketing_emails',
];
const MANDATORY_FIELDS = ['security_alerts', 'account_notifications'];

const FIELD_I18N_KEY = {
  weekly_digest: 'weeklyDigest',
  community_updates: 'communityUpdates',
  price_alerts: 'priceAlerts',
  beta_invitations: 'betaInvitations',
  marketing_emails: 'marketingEmails',
  security_alerts: 'securityAlerts',
  account_notifications: 'accountNotifications',
};

const Toggle = ({ checked, onChange, disabled, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
      checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const NotificationsSettingsPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { prefs, loading, error, updating, updateError } = useSelector((state) => state.emailPreferences);

  const [local, setLocal] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!prefs) dispatch(fetchEmailPreferences());
  }, [dispatch, prefs]);

  useEffect(() => {
    if (prefs) setLocal(prefs);
  }, [prefs]);

  const hasChanges = useMemo(() => {
    if (!local || !prefs) return false;
    return [...TOGGLEABLE_FIELDS].some((f) => local[f] !== prefs[f]);
  }, [local, prefs]);

  const handleToggle = (field, value) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!local || !hasChanges) return;
    const patch = Object.fromEntries(TOGGLEABLE_FIELDS.map((f) => [f, local[f]]));
    try {
      await dispatch(updateEmailPreferences(patch)).unwrap();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      // error already surfaced via updateError
    }
  };

  const handleCancel = () => setLocal(prefs);

  const displayError = error || updateError;

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('notificationSettings.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">{t('notificationSettings.subtitle')}</p>
      </div>

      <div className="max-w-3xl space-y-6">
        {showSuccess && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600/30 rounded-lg p-4 flex items-center">
            <Check className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
            <p className="text-green-700 dark:text-green-400">{t('notificationSettings.savedSuccess')}</p>
          </div>
        )}

        {displayError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-600/30 rounded-lg p-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-400">{displayError}</p>
            </div>
            <button
              onClick={() => dispatch(clearEmailPreferencesError())}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              ✕
            </button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-4">
            <Bell className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('sidebar.notifications')}</h2>
          </div>

          {loading || !local ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {TOGGLEABLE_FIELDS.map((field) => (
                <div key={field} className="flex items-center justify-between px-6 py-4">
                  <div className="pr-4">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {t(`notificationSettings.fields.${FIELD_I18N_KEY[field]}.title`)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t(`notificationSettings.fields.${FIELD_I18N_KEY[field]}.description`)}
                    </div>
                  </div>
                  <Toggle
                    checked={!!local[field]}
                    onChange={(v) => handleToggle(field, v)}
                    disabled={updating}
                    ariaLabel={t(`notificationSettings.fields.${FIELD_I18N_KEY[field]}.title`)}
                  />
                </div>
              ))}

              {MANDATORY_FIELDS.map((field) => (
                // opacity-70 previously diluted the already-muted gray text
                // below WCAG AA contrast once composited onto the white
                // background (axe measured the *effective* rendered color,
                // not the source Tailwind class) -- de-emphasis is conveyed
                // by the lock icon, muted tones, and disabled toggle instead.
                <div key={field} className="flex items-center justify-between px-6 py-4">
                  <div className="pr-4">
                    <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      {t(`notificationSettings.fields.${FIELD_I18N_KEY[field]}.title`)}
                      <Lock className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {t(`notificationSettings.fields.${FIELD_I18N_KEY[field]}.description`)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-500 mt-0.5">
                      {t('notificationSettings.mandatoryNote')}
                    </div>
                  </div>
                  <Toggle
                    checked={true}
                    onChange={() => {}}
                    disabled
                    ariaLabel={t(`notificationSettings.fields.${FIELD_I18N_KEY[field]}.title`)}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="p-6 flex justify-end space-x-4">
            <button
              className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              onClick={handleCancel}
              disabled={!hasChanges || updating}
            >
              {t('notificationSettings.cancel')}
            </button>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              onClick={handleSave}
              disabled={!hasChanges || updating}
            >
              {updating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('notificationSettings.saving')}
                </>
              ) : (
                t('notificationSettings.saveChanges')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsSettingsPage;
